import React from "react";
import "./DeviceArt.css";

const PCB = "#163b2d";
const PCB_STROKE = "rgba(93, 242, 164, 0.55)";
const BOARD_BLUE = "#16283a";
const CHIP = "#0b0e12";
const CHIP_STROKE = "rgba(255, 255, 255, 0.3)";
const GOLD = "#c9a44a";
const PORT = "#2b3440";
const METAL = "#1a222b";
const METAL_STROKE = "rgba(255, 255, 255, 0.22)";
const FIN = "rgba(255, 255, 255, 0.18)";

const T = (props) => (
  <text
    fontFamily="JetBrains Mono, monospace"
    fontSize="7"
    fill="rgba(255,255,255,0.7)"
    textAnchor="middle"
    letterSpacing="0.6"
    {...props}
  />
);

function Frame({ children, label }) {
  return (
    <svg className="da" viewBox="0 0 200 120" aria-hidden="true" focusable="false">
      {children}
      {label && (
        <T x="100" y="113" fill="rgba(255,255,255,0.45)" fontSize="6.5">
          {label}
        </T>
      )}
    </svg>
  );
}

function Jetson({ chip = "ORIN", label }) {
  return (
    <Frame label={label}>
      <rect x="40" y="34" width="120" height="60" rx="5" fill={PCB} stroke={PCB_STROKE} />
      {[0, 1, 2, 3].map((i) => (
        <rect key={i} x={46 + i * 7} y="80" width="4" height="6" rx="1" fill="rgba(255,255,255,.18)" />
      ))}
      <rect x="120" y="76" width="30" height="10" rx="2" fill={CHIP} stroke={CHIP_STROKE} />
      <rect x="54" y="18" width="92" height="34" rx="3" fill={METAL} stroke={METAL_STROKE} />
      {Array.from({ length: 9 }, (_, i) => (
        <line key={i} x1={60 + i * 10} y1="20" x2={60 + i * 10} y2="50" stroke={FIN} />
      ))}
      <rect x="84" y="56" width="32" height="20" rx="2" fill={CHIP} stroke={CHIP_STROKE} />
      <T x="100" y="69">{chip}</T>
      {Array.from({ length: 18 }, (_, i) => (
        <rect key={i} x={48 + i * 6} y="90" width="3.5" height="5" fill={GOLD} opacity="0.85" />
      ))}
    </Frame>
  );
}

function DevKit({ chip = "AGX", label }) {
  return (
    <Frame label={label}>
      <rect x="38" y="20" width="124" height="80" rx="10" fill={METAL} stroke={METAL_STROKE} />
      <circle cx="100" cy="60" r="27" fill={CHIP} stroke={CHIP_STROKE} />
      {[0, 60, 120, 180, 240, 300].map((a) => (
        <path
          key={a}
          d="M100 60 L118 50 Q122 60 118 70 Z"
          fill="rgba(255,255,255,0.14)"
          transform={`rotate(${a} 100 60)`}
        />
      ))}
      <circle cx="100" cy="60" r="6" fill="var(--accent)" opacity="0.9" />
      {[0, 1, 2].map((i) => (
        <rect key={i} x="44" y={30 + i * 14} width="10" height="8" rx="1.5" fill={PORT} />
      ))}
      <rect x="148" y="30" width="6" height="40" rx="1" fill="rgba(255,255,255,0.1)" />
      <T x="100" y="94" fontSize="6.5">{chip}</T>
    </Frame>
  );
}

function Pi({ label, hat = "HAILO" }) {
  return (
    <Frame label={label}>
      <rect x="34" y="40" width="122" height="64" rx="5" fill={PCB} stroke={PCB_STROKE} />
      <rect x="150" y="48" width="14" height="16" rx="1.5" fill={PORT} />
      <rect x="150" y="70" width="14" height="16" rx="1.5" fill={PORT} />
      <rect x="130" y="92" width="18" height="10" rx="1.5" fill={PORT} />
      {Array.from({ length: 20 }, (_, i) => (
        <React.Fragment key={i}>
          <circle cx={42 + i * 5} cy="46" r="1.3" fill={GOLD} />
          <circle cx={42 + i * 5} cy="51" r="1.3" fill={GOLD} />
        </React.Fragment>
      ))}
      <rect x="70" y="64" width="24" height="24" rx="2" fill={CHIP} stroke={CHIP_STROKE} />
      <rect x="104" y="66" width="18" height="12" rx="1.5" fill={CHIP} stroke={CHIP_STROKE} />
      {hat && (
        <>
          <line x1="50" y1="40" x2="50" y2="28" stroke="rgba(255,255,255,0.25)" />
          <line x1="140" y1="40" x2="140" y2="28" stroke="rgba(255,255,255,0.25)" />
          <rect x="44" y="14" width="102" height="22" rx="4" fill="#153327" stroke={PCB_STROKE} />
          <rect x="84" y="18" width="30" height="14" rx="2" fill={CHIP} stroke={CHIP_STROKE} />
          <T x="99" y="28" fontSize="6">{hat}</T>
        </>
      )}
    </Frame>
  );
}

function Sbc({ chip = "RK3588", label }) {
  return (
    <Frame label={label}>
      <rect x="34" y="30" width="132" height="72" rx="5" fill={BOARD_BLUE} stroke="rgba(79,211,255,0.5)" />
      <rect x="150" y="40" width="12" height="14" rx="1.5" fill={PORT} />
      <rect x="150" y="62" width="12" height="14" rx="1.5" fill={PORT} />
      <rect x="40" y="88" width="26" height="8" rx="1.5" fill={PORT} />
      <rect x="72" y="44" width="46" height="34" rx="3" fill={METAL} stroke={METAL_STROKE} />
      {Array.from({ length: 5 }, (_, i) => (
        <line key={i} x1={78 + i * 9} y1="46" x2={78 + i * 9} y2="76" stroke={FIN} />
      ))}
      <T x="95" y="92" fontSize="6.5">{chip}</T>
      {Array.from({ length: 12 }, (_, i) => (
        <circle key={i} cx={44 + i * 5} cy="36" r="1.2" fill={GOLD} />
      ))}
    </Frame>
  );
}

function BoxPc({ label, tag = "x86" }) {
  return (
    <Frame label={label}>
      <rect x="34" y="30" width="132" height="64" rx="6" fill={METAL} stroke={METAL_STROKE} />
      {Array.from({ length: 11 }, (_, i) => (
        <rect key={i} x={44 + i * 8} y="38" width="3" height="24" rx="1" fill="rgba(255,255,255,0.12)" />
      ))}
      <rect x="110" y="74" width="12" height="9" rx="1" fill={PORT} />
      <rect x="126" y="74" width="12" height="9" rx="1" fill={PORT} />
      <rect x="142" y="74" width="16" height="9" rx="1" fill={PORT} />
      <circle cx="46" cy="79" r="2.6" fill="var(--accent)" />
      <T x="54" y="82" fontSize="6" textAnchor="start">{tag}</T>
    </Frame>
  );
}

function UsbStick({ label, tag = "TPU" }) {
  return (
    <Frame label={label}>
      <rect x="52" y="44" width="86" height="34" rx="9" fill="#e6ebee" stroke="rgba(0,0,0,0.4)" />
      <rect x="138" y="52" width="26" height="18" rx="2" fill="#8a949e" />
      <rect x="144" y="56" width="16" height="10" rx="1" fill="#5b6570" />
      <T x="95" y="65" fill="#1a222b" fontSize="8">{tag}</T>
      <circle cx="62" cy="61" r="2.2" fill="var(--accent)" />
    </Frame>
  );
}

function Soc({ label, chip = "QRB5165" }) {
  return (
    <Frame label={label}>
      <rect x="34" y="32" width="132" height="70" rx="5" fill="#1b2230" stroke="rgba(255,255,255,0.25)" />
      <rect x="66" y="46" width="44" height="40" rx="3" fill={CHIP} stroke={CHIP_STROKE} />
      <T x="88" y="69" fontSize="6">{chip}</T>
      {[0, 1, 2].map((i) => (
        <circle key={i} cx={140} cy={44 + i * 16} r="4" fill="none" stroke={GOLD} strokeWidth="1.5" />
      ))}
      <rect x="120" y="82" width="34" height="10" rx="1.5" fill={PORT} />
      <rect x="40" y="88" width="18" height="8" rx="1.5" fill={PORT} />
    </Frame>
  );
}

function M2Card({ label, chip = "HAILO-8" }) {
  return (
    <Frame label={label}>
      <rect x="28" y="46" width="132" height="28" rx="2" fill={PCB} stroke={PCB_STROKE} />
      <rect x="66" y="51" width="46" height="18" rx="2" fill={CHIP} stroke={CHIP_STROKE} />
      <T x="89" y="63" fontSize="6.5">{chip}</T>
      {Array.from({ length: 6 }, (_, i) => (
        <rect key={i} x={142 + i * 4} y="52" width="2.4" height="16" fill={GOLD} opacity="0.85" />
      ))}
      <rect x="152" y="58" width="6" height="4" fill="#07090b" />
      <circle cx="36" cy="60" r="3" fill="none" stroke="rgba(255,255,255,0.3)" />
      {[0, 1, 2].map((i) => (
        <rect key={i} x={40 + i * 7} y="66" width="4" height="4" fill="rgba(255,255,255,0.18)" />
      ))}
    </Frame>
  );
}

function Server({ label }) {
  return (
    <Frame label={label}>
      {[0, 1, 2].map((i) => (
        <g key={i} transform={`translate(0 ${i * 26})`}>
          <rect x="36" y="22" width="128" height="22" rx="3" fill={METAL} stroke={METAL_STROKE} />
          {[0, 1, 2, 3, 4, 5].map((j) => (
            <rect key={j} x={44 + j * 14} y="27" width="10" height="12" rx="1" fill="rgba(255,255,255,0.08)" />
          ))}
          <circle cx="140" cy="33" r="2.2" fill="var(--accent)" className={`da__led da__led--${i}`} />
          <circle cx="150" cy="33" r="2.2" fill="var(--amber)" opacity="0.7" />
        </g>
      ))}
    </Frame>
  );
}

function CloudGpu({ label, tag = "GPU" }) {
  return (
    <Frame label={label}>
      <path
        d="M62 84a20 20 0 0 1-3-39.6A30 30 0 0 1 116 40a22 22 0 0 1-2 44z"
        fill="rgba(79,211,255,0.08)"
        stroke="rgba(79,211,255,0.6)"
        strokeWidth="1.5"
      />
      <rect x="76" y="54" width="46" height="22" rx="3" fill={CHIP} stroke={CHIP_STROKE} />
      <T x="99" y="68" fontSize="7">{tag}</T>
      {[0, 1, 2, 3].map((i) => (
        <rect key={i} x={80 + i * 11} y="76" width="6" height="4" fill={GOLD} opacity="0.8" />
      ))}
    </Frame>
  );
}

function Shield({ label, tag = "EU" }) {
  return (
    <Frame label={label}>
      <path
        d="M100 22l36 12v26c0 22-16 36-36 42-20-6-36-20-36-42V34z"
        fill="rgba(93,242,164,0.08)"
        stroke="rgba(93,242,164,0.6)"
        strokeWidth="1.5"
      />
      <T x="100" y="66" fontSize="12" fill="var(--accent)">{tag}</T>
    </Frame>
  );
}

function Tower({ label, tag = "GPU" }) {
  return (
    <Frame label={label}>
      <rect x="62" y="12" width="76" height="94" rx="7" fill={METAL} stroke={METAL_STROKE} />
      <rect x="70" y="20" width="60" height="8" rx="2" fill="rgba(255,255,255,0.08)" />
      {Array.from({ length: 5 }, (_, i) => (
        <line key={i} x1="70" y1={36 + i * 5} x2="130" y2={36 + i * 5} stroke={FIN} />
      ))}
      <rect x="70" y="66" width="60" height="28" rx="3" fill={CHIP} stroke="rgba(93,242,164,0.5)" />
      <circle cx="84" cy="80" r="8" fill="none" stroke="rgba(255,255,255,0.25)" />
      <circle cx="84" cy="80" r="2" fill="rgba(255,255,255,0.35)" />
      <T x="110" y="83" fontSize="6.5" fill="var(--accent)">{tag}</T>
      <circle cx="126" cy="24" r="2.2" fill="var(--accent)" />
    </Frame>
  );
}

function GpuCard({ label, tag = "GPU" }) {
  return (
    <Frame label={label}>
      <rect x="28" y="34" width="132" height="48" rx="5" fill={METAL} stroke={METAL_STROKE} />
      {[60, 104].map((cx) => (
        <g key={cx}>
          <circle cx={cx} cy="58" r="16" fill={CHIP} stroke="rgba(255,255,255,0.25)" />
          {[0, 60, 120, 180, 240, 300].map((a) => (
            <path key={a} d={`M${cx} 58 L${cx + 11} 52 Q${cx + 13} 58 ${cx + 11} 64 Z`} fill="rgba(255,255,255,0.12)" transform={`rotate(${a} ${cx} 58)`} />
          ))}
          <circle cx={cx} cy="58" r="3" fill="var(--accent)" opacity="0.9" />
        </g>
      ))}
      <rect x="160" y="28" width="8" height="60" rx="1" fill="#3a4652" />
      <rect x="163" y="34" width="2" height="10" fill="rgba(0,0,0,0.5)" />
      {Array.from({ length: 14 }, (_, i) => (
        <rect key={i} x={38 + i * 7} y="82" width="4" height="6" fill={GOLD} opacity="0.85" />
      ))}
      <T x="132" y="44" fontSize="6" fill="rgba(255,255,255,0.7)">{tag}</T>
    </Frame>
  );
}

function CameraArt({ label, tag = "AI" }) {
  return (
    <Frame label={label}>
      <path d="M134 60h18v-22" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
      <rect x="148" y="32" width="10" height="6" rx="1" fill="#3a4652" />
      <rect x="46" y="40" width="90" height="40" rx="12" fill={METAL} stroke={METAL_STROKE} />
      <circle cx="68" cy="60" r="13" fill={CHIP} stroke="rgba(255,255,255,0.3)" />
      <circle cx="68" cy="60" r="6" fill="rgba(79,211,255,0.75)" />
      <circle cx="65" cy="57" r="1.8" fill="rgba(255,255,255,0.7)" />
      <rect x="90" y="51" width="38" height="18" rx="3" fill={CHIP} stroke="rgba(93,242,164,0.5)" />
      <T x="109" y="63" fontSize="6" fill="var(--accent)">{tag}</T>
      <circle cx="56" cy="46" r="1.8" fill="var(--danger)" />
    </Frame>
  );
}

function Generic({ label }) {
  return (
    <Frame label={label}>
      <rect
        x="40"
        y="30"
        width="120"
        height="64"
        rx="8"
        fill="none"
        stroke="rgba(255,255,255,0.35)"
        strokeDasharray="5 4"
        strokeWidth="1.5"
      />
      <T x="100" y="60" fontSize="9" fill="rgba(255,255,255,0.7)">YOUR HW</T>
      <T x="100" y="74" fontSize="6" fill="rgba(255,255,255,0.4)">x86 · ARM · GPU</T>
    </Frame>
  );
}

const KINDS = {
  jetson: Jetson,
  devkit: DevKit,
  pi: Pi,
  sbc: Sbc,
  box: BoxPc,
  usb: UsbStick,
  soc: Soc,
  m2: M2Card,
  server: Server,
  cloud: CloudGpu,
  shield: Shield,
  tower: Tower,
  gpucard: GpuCard,
  camera: CameraArt,
  generic: Generic,
};

/**
 * Stylised line-art of an edge device or cloud target. Pass `image` to show a
 * real product photo instead (drop files into /public/hardware and set the
 * `image` field in src/data/hardware.js).
 */
export default function DeviceArt({ kind = "generic", image, alt = "", label, ...rest }) {
  if (image) {
    return <img className="da da--img" src={image} alt={alt} loading="lazy" decoding="async" />;
  }
  const Cmp = KINDS[kind] || Generic;
  return <Cmp label={label} {...rest} />;
}
